import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { SelectLayer } from "../../components/CommonInputs";
import { MapitStateContext } from "src/states/MapitState";
import { MapitUtils } from "src/managers/MapitUtils";
import { JPBReferenceProperty, JPBAssumptions, AreaUnit, SoilQualityGroup, SOIL_QUALITY_GROUPS, getSoilTypeColor, AverageCalculationMethod, JPBReportPolygonAnalysis } from "./JPBState";
import { formatPricePerSquareMeter, formatArea, formatPercentage, calculateSoilSpecificAgriculturalPrices, calculateAreaPrice, mapSoilTypeToGroup, calculateWeightedMeanForSeries, getResolvedJPBAssumptions, getSoilQualityFactorsByGroup, getNonAgricultureToAgricultureRatio } from "./JPBFunc";
import { area, bbox, combine, intersect, featureCollection } from "@turf/turf";
import { GlassInputGroup } from "../../components/GlassInput";
import { Spinner } from "react-bootstrap";
import { LayerInfo, LayerVariabilityType } from "src/common/managers/Types";
import type { GeoJSONSource } from "maplibre-gl";
import { DataCollector } from "src/dataCollector/DataCollector";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { JPBAnalyzerGeometryData, JPBAnalyzerResult, useJPBSessionSlice } from "./JPBSessionState";

const SOURCE_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland';
const SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort';
const SOURCE_ID_POLYGON_ANALYZER_PROPERTY = 'jpb-polygon-analyzer-property';
const LAYER_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland-layer';
const LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort-layer';
const LAYER_ID_POLYGON_ANALYZER_PROPERTY_OUTLINE = 'jpb-polygon-analyzer-property-outline';

/** Same as JPB search buffer outline (`JPBSearchArea` / `addSearchAreaOutline`); stroke is thicker for visibility. */
const JPB_ANALYZER_PROPERTY_OUTLINE_COLOR = '#d6c251';
const JPB_ANALYZER_PROPERTY_OUTLINE_WIDTH_PX = 4;

function collectLngLatsFromPointLayer(layer: LayerInfo): [number, number][] {
   const out: [number, number][] = [];
   const gj = layer.geoJson;
   if (!gj?.features?.length) {
      return out;
   }
   for (const f of gj.features) {
      const g = f.geometry as { type: string; coordinates?: unknown } | undefined;
      if (!g) {
         continue;
      }
      if (g.type === "Point" && Array.isArray(g.coordinates)) {
         const c = g.coordinates as number[];
         if (c.length >= 2) {
            out.push([c[0], c[1]]);
         }
      } else if (g.type === "MultiPoint" && Array.isArray(g.coordinates)) {
         for (const c of g.coordinates as number[][]) {
            if (c && c.length >= 2) {
               out.push([c[0], c[1]]);
            }
         }
      }
   }
   return out;
}

async function resolveSfeLokalIdFromLngLat(lng: number, lat: number): Promise<number | null> {
   try {
      const utm = GenerateGeom.convertFromLatLngToUTM({ lat, lng });
      const res = await PropertyInfoInterface.getMatrikelData(utm);
      const raw = res?.features?.[0]?.properties?.samletfastejendomlokalid;
      if (raw !== undefined && raw !== null && raw !== "") {
         const n = Number(raw);
         return Number.isFinite(n) ? n : null;
      }
   } catch {
      /* try WFS SFE at point */
   }
   try {
      const props = await PropertyInfoInterface.getSFEFromPoint({ lat, lng });
      const raw = props?.BFEnummer ?? props?.samletFastEjendomLokalId ?? props?.samletfastejendomlokalid;
      if (raw !== undefined && raw !== null && raw !== "") {
         const n = Number(raw);
         return Number.isFinite(n) ? n : null;
      }
   } catch {
      /* no SFE */
   }
   return null;
}

async function fetchCombinedSfePolygonFromPointLayer(layer: LayerInfo): Promise<{ feature: any | null; error?: string }> {
   const lngLats = collectLngLatsFromPointLayer(layer);
   if (lngLats.length === 0) {
      return {
         feature: null,
         error: Localization.getText("No point geometry in the selected layer")
      };
   }
   const seenIds = new Set<number>();
   const orderedSfeIds: number[] = [];
   for (const [lng, lat] of lngLats) {
      const sfeId = await resolveSfeLokalIdFromLngLat(lng, lat);
      if (sfeId === null) {
         return { feature: null, error: Localization.getText("No cadaster data for this point") };
      }
      if (!seenIds.has(sfeId)) {
         seenIds.add(sfeId);
         orderedSfeIds.push(sfeId);
      }
   }
   const polygonFeatures: any[] = [];
   for (const sfeId of orderedSfeIds) {
      const fc = await PropertyInfoInterface.getGeojsonOfSFE(sfeId);
      if (!fc?.features?.length) {
         return { feature: null, error: Localization.getText("No geometry data for this property") };
      }
      for (const f of fc.features) {
         const geom = f?.geometry;
         if (geom && (geom.type === "Polygon" || geom.type === "MultiPolygon")) {
            polygonFeatures.push(f);
         }
      }
   }
   if (polygonFeatures.length === 0) {
      return { feature: null, error: Localization.getText("No geometry data for this property") };
   }
   if (polygonFeatures.length === 1) {
      return { feature: polygonFeatures[0], error: undefined };
   }
   try {
      const combined = combine(featureCollection(polygonFeatures as any) as any);
      if (combined?.features?.length > 0 && combined.features[0]?.geometry) {
         return {
            feature: {
               type: "Feature",
               geometry: combined.features[0].geometry,
               properties: {}
            },
            error: undefined
         };
      }
   } catch (e) {
      console.error("JPBPolygonAnalyzer: combine SFE polygons failed", e);
   }
   return { feature: polygonFeatures[0], error: undefined };
}

const yieldToMainThread = () => new Promise<void>((resolve) => {
   if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
   }
   window.setTimeout(() => resolve(), 0);
});

function bboxesOverlap(a: [number, number, number, number], b: [number, number, number, number]): boolean {
   return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

function toFarmlandParts(farmlandGeometry: any): Array<{ feature: any; bbox: [number, number, number, number] }> {
   if (!farmlandGeometry) return [];
   if (farmlandGeometry.type === "Polygon") {
      const feature = {
         type: "Feature" as const,
         geometry: farmlandGeometry,
         properties: {}
      };
      return [{ feature, bbox: bbox(feature as any) as [number, number, number, number] }];
   }
   if (farmlandGeometry.type === "MultiPolygon" && Array.isArray(farmlandGeometry.coordinates)) {
      return farmlandGeometry.coordinates.map((polygonCoords: any) => {
         const feature = {
            type: "Feature" as const,
            geometry: {
               type: "Polygon",
               coordinates: polygonCoords
            },
            properties: {}
         };
         return { feature, bbox: bbox(feature as any) as [number, number, number, number] };
      });
   }
   const fallbackFeature = {
      type: "Feature" as const,
      geometry: farmlandGeometry,
      properties: {}
   };
   return [{ feature: fallbackFeature, bbox: bbox(fallbackFeature as any) as [number, number, number, number] }];
}

async function runWithYield<T>(work: () => T): Promise<T> {
   await yieldToMainThread();
   const result = work();
   await yieldToMainThread();
   return result;
}

function getFarmlandLayerYear(transactionDate: Date): number {
   const transactionYear = transactionDate.getFullYear();
   const earliestAvailableYear = 2008;
   const latestAvailableYear = 2025;

   if (transactionYear >= earliestAvailableYear && transactionYear <= latestAvailableYear) {
      return transactionYear;
   }

   if (transactionYear > latestAvailableYear) {
      return latestAvailableYear;
   }

   if (transactionYear < earliestAvailableYear) {
      return earliestAvailableYear;
   }

   return earliestAvailableYear;
}

async function fetchFarmlandOverlap(
   geoshape: any,
   transactionDate: Date,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerYear = getFarmlandLayerYear(transactionDate);
   const layerName = `Marker_${layerYear}` as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureIntersect(
         "FVM (Føde, Land og Fisk)",
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      const geometries = result.features.map((feature: any) => feature.geometry);

      if (geometries.length === 0) {
         return null;
      }

      const farmlandFeatureCollection = {
         type: "FeatureCollection" as const,
         features: geometries.map((geom: any) => ({
            type: "Feature" as const,
            geometry: geom,
            properties: {}
         }))
      };

      const combined = combine(farmlandFeatureCollection as any);

      if (combined && combined.features && combined.features.length > 0) {
         const firstFeature = combined.features[0] as any;
         return firstFeature?.geometry || null;
      }

      return null;
   } catch (error) {
      console.error("Error fetching farmland overlap:", error);
      throw error;
   }
}

function calculateFarmlandIntersection(
   geoshape: any,
   farmlandGeometry: any
): { intersectionGeometry: any; overlapPercentage: number } | null {
   if (!geoshape || !farmlandGeometry) {
      return null;
   }

   try {
      const combinedFeatureCollection = featureCollection([
         {
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         },
         {
            type: "Feature" as const,
            geometry: farmlandGeometry,
            properties: {}
         }
      ]);

      const intersection = intersect(combinedFeatureCollection);

      if (!intersection) {
         return {
            intersectionGeometry: null,
            overlapPercentage: 0
         };
      }

      const intersectionGeometry = intersection.geometry;

      const intersectionArea = area(intersection);
      const propertyArea = area({
         type: "Feature" as const,
         geometry: geoshape,
         properties: {}
      });

      if (propertyArea === 0) {
         return null;
      }

      const overlapPercentage = (intersectionArea / propertyArea) * 100;

      return {
         intersectionGeometry,
         overlapPercentage
      };
   } catch (error) {
      console.error("Error calculating farmland intersection:", error);
      return null;
   }
}

async function calculateJordbundskortFarmlandIntersection(
   farmlandGeometry: any,
   jordbundskortGeometry: any
): Promise<{ intersectionGeometry: any | null; overlapPercentages: { [soilType: string]: number } } | null> {
   if (!farmlandGeometry || !jordbundskortGeometry) {
      return null;
   }

   try {
      if (jordbundskortGeometry.type !== "FeatureCollection" || !jordbundskortGeometry.features) {
         return null;
      }

      const intersectionFeatures: any[] = [];
      const overlapPercentages: { [soilType: string]: number } = {};
      const farmlandParts = toFarmlandParts(farmlandGeometry);
      if (!farmlandParts.length) {
         return null;
      }

      const farmlandArea = area({
         type: "Feature" as const,
         geometry: farmlandGeometry,
         properties: {}
      });

      if (farmlandArea === 0) {
         return null;
      }

      const jordbundskortFeatures = jordbundskortGeometry.features as any[];
      for (let i = 0; i < jordbundskortFeatures.length; i++) {
         // Yield frequently so the browser can paint and remain interactive.
         if (i > 0 && i % 2 === 0) {
            await yieldToMainThread();
         }
         const jordbundskortFeature = jordbundskortFeatures[i];
         if (!jordbundskortFeature.geometry) {
            continue;
         }

         try {
            const jordbundFeatureForIntersect = {
               type: "Feature",
               geometry: jordbundskortFeature.geometry,
               properties: {}
            } as any;
            const jordbundskortBBox = bbox(jordbundFeatureForIntersect);
            const candidateFarmlandParts = farmlandParts.filter((part) =>
               bboxesOverlap(part.bbox, jordbundskortBBox as [number, number, number, number])
            );
            if (!candidateFarmlandParts.length) {
               continue;
            }

            let totalFeatureIntersectionArea = 0;
            const featureIntersections: any[] = [];
            for (let j = 0; j < candidateFarmlandParts.length; j++) {
               const combinedFeatureCollection = featureCollection([
                  candidateFarmlandParts[j].feature,
                  jordbundFeatureForIntersect
               ]) as any;
               const intersection = intersect(combinedFeatureCollection as any);
               if (!intersection || !intersection.geometry) {
                  continue;
               }
               const intersectionArea = area(intersection);
               if (!(intersectionArea > 0)) {
                  continue;
               }
               totalFeatureIntersectionArea += intersectionArea;
               featureIntersections.push(intersection);
            }
            if (featureIntersections.length === 0 || totalFeatureIntersectionArea <= 0) {
               continue;
            }

            const overlapPercentage = (totalFeatureIntersectionArea / farmlandArea) * 100;

            const jordtype = jordbundskortFeature.properties?.Jordtype ||
               jordbundskortFeature.properties?.jordtype ||
               'Unknown';

            if (overlapPercentages[jordtype]) {
               overlapPercentages[jordtype] += overlapPercentage;
            } else {
               overlapPercentages[jordtype] = overlapPercentage;
            }

            featureIntersections.forEach((intersection) => {
               intersectionFeatures.push({
                  type: "Feature" as const,
                  geometry: intersection.geometry,
                  properties: {
                     ...jordbundskortFeature.properties
                  }
               });
            });
         } catch (error) {
            console.error("Error calculating intersection for jordbundskort feature:", error);
         }
      }

      if (intersectionFeatures.length === 0) {
         return null;
      }

      const totalPercentage = Object.values(overlapPercentages).reduce((sum, p) => sum + (typeof p === 'number' ? p : 0), 0);

      if (totalPercentage > 0 && Math.abs(totalPercentage - 100) > 0.01) {
         const normalizationFactor = 100 / totalPercentage;
         for (const jordtype in overlapPercentages) {
            if (typeof overlapPercentages[jordtype] === 'number') {
               overlapPercentages[jordtype] = overlapPercentages[jordtype] * normalizationFactor;
            }
         }
      }

      return {
         intersectionGeometry: {
            type: "FeatureCollection" as const,
            features: intersectionFeatures
         },
         overlapPercentages
      };
   } catch (error) {
      console.error("Error calculating jordbundskort-farmland intersection:", error);
      return null;
   }
}

async function fetchJordbundskortOverlap(
   geoshape: any,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerName = "Jordbundskort_2024" as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureIntersect(
         "FVM (Føde, Land og Fisk)",
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      return {
         type: "FeatureCollection" as const,
         features: result.features.map((feature: any) => ({
            type: "Feature" as const,
            geometry: feature.geometry,
            properties: feature.properties || {}
         }))
      };
   } catch (error) {
      console.error("Error fetching jordbundskort overlap:", error);
      throw error;
   }
}

type JPBPolygonAnalyzerProps = {
   referenceProperties: JPBReferenceProperty[];
   checkedItems: Set<string>;
   areaUnit: AreaUnit;
   averageCalculationMethod: AverageCalculationMethod;
   customAssumptions?: Partial<JPBAssumptions>;
   settingsRefreshToken?: number;
   /** When set, analyzer uses this map layer and hides the layer picker (JPB simple mode). */
   lockedAnalyzerLayerId?: number;
   /** Subject address resolved by the parent (simple-mode address line, advanced layer name, etc.). */
   subjectAddress?: string;
   /** Notified whenever the analysis becomes available or clears (`null` = no usable result). */
   onAnalysisChange?: (analysis: JPBReportPolygonAnalysis | null) => void;
}

function SoilDistributionBar(props: {
   analysisResult: JPBAnalyzerResult;
   hoveredSoilType: string | null;
   hoveredSoilPercentage: number | null;
   hoveredNonFarmland: boolean;
   setHoveredSoilType: (type: string | null) => void;
   setHoveredSoilPercentage: (percentage: number | null) => void;
   setHoveredNonFarmland: (hovered: boolean) => void;
}) {
   const { analysisResult, hoveredSoilType, hoveredSoilPercentage, hoveredNonFarmland, setHoveredSoilType, setHoveredSoilPercentage, setHoveredNonFarmland } = props;
   const { root } = useFloatingPortalContext();
   const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
   const [nonFarmlandReferenceElement, setNonFarmlandReferenceElement] = useState<HTMLElement | null>(null);

   const boundaryMiddleware =
      root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })];

   const { refs: tooltipRefs, floatingStyles: tooltipFloatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredSoilType !== null,
      middleware: [offset(8), ...boundaryMiddleware],
      placement: 'top',
      elements: {
         reference: referenceElement
      }
   });

   const { refs: nonFarmlandTooltipRefs, floatingStyles: nonFarmlandTooltipFloatingStyles, context: nonFarmlandTooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredNonFarmland,
      middleware: [offset(8), ...boundaryMiddleware],
      placement: 'top',
      elements: {
         reference: nonFarmlandReferenceElement
      }
   });

   const { getFloatingProps: getTooltipFloatingProps } = useInteractions([
      useDismiss(tooltipContext)
   ]);

   const { getFloatingProps: getNonFarmlandTooltipFloatingProps } = useInteractions([
      useDismiss(nonFarmlandTooltipContext)
   ]);

   return (
      <div style={{ marginTop: "20px" }}>
         <div style={{ marginBottom: "10px", fontWeight: "600" }}>
            {Localization.getText("Area Distribution")}
         </div>
         <div style={{
            width: "100%",
            height: "30px",
            backgroundColor: "#e0e0e0",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
            position: "relative"
         }}>
            <div style={{
               width: `${analysisResult.farmlandOverlapPercentage}%`,
               backgroundColor: "#4CAF50",
               position: "relative",
               display: "flex"
            }}>
               {Object.entries(analysisResult.soilComposition).map(([soilType, percentage], index, arr) => {
                  const cumulativePercentage = arr.slice(0, index).reduce((sum, [, p]) => sum + p, 0);
                  const width = percentage;
                  const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                  const soilColor = getSoilTypeColor(soilType);
                  const isHovered = hoveredSoilType === soilType;

                  return (
                     <div
                        key={soilType}
                        ref={(el) => {
                           if (isHovered && el) {
                              setReferenceElement(el);
                              tooltipRefs.setReference(el);
                           }
                        }}
                        style={{
                           position: "absolute",
                           left: `${cumulativePercentage}%`,
                           width: `${width}%`,
                           height: "100%",
                           backgroundColor: soilColor,
                           borderRight: index < arr.length - 1 ? "1px solid rgba(0,0,0,0.1)" : "none",
                           cursor: "pointer",
                           opacity: isHovered ? 0.9 : 1,
                           transition: "opacity 0.2s"
                        }}
                        onMouseEnter={(e) => {
                           setReferenceElement(e.currentTarget);
                           tooltipRefs.setReference(e.currentTarget);
                           setHoveredSoilType(soilType);
                           setHoveredSoilPercentage(percentageOfTotal);
                        }}
                        onMouseLeave={() => {
                           setHoveredSoilType(null);
                           setHoveredSoilPercentage(null);
                           setReferenceElement(null);
                        }}
                     />
                  );
               })}
            </div>
            <div
               ref={(el) => {
                  if (hoveredNonFarmland && el) {
                     setNonFarmlandReferenceElement(el);
                     nonFarmlandTooltipRefs.setReference(el);
                  }
               }}
               style={{
                  width: `${100 - analysisResult.farmlandOverlapPercentage}%`,
                  backgroundColor: "#9E9E9E",
                  cursor: "pointer",
                  opacity: hoveredNonFarmland ? 0.9 : 1,
                  transition: "opacity 0.2s"
               }}
               onMouseEnter={(e) => {
                  setNonFarmlandReferenceElement(e.currentTarget);
                  nonFarmlandTooltipRefs.setReference(e.currentTarget);
                  setHoveredNonFarmland(true);
               }}
               onMouseLeave={() => {
                  setHoveredNonFarmland(false);
                  setNonFarmlandReferenceElement(null);
               }}
            />
         </div>
         {hoveredSoilType !== null && hoveredSoilPercentage !== null && (
            <MITFloatingPortal>
               <div
                  ref={tooltipRefs.setFloating}
                  style={{
                     ...tooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>{hoveredSoilType}</div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(hoveredSoilPercentage)}
                  </div>
               </div>
            </MITFloatingPortal>
         )}
         {hoveredNonFarmland && (
            <MITFloatingPortal>
               <div
                  ref={nonFarmlandTooltipRefs.setFloating}
                  style={{
                     ...nonFarmlandTooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getNonFarmlandTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>
                     {Localization.getText("Non-Farmland")}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                  </div>
               </div>
            </MITFloatingPortal>
         )}
         <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px", fontSize: "12px", color: "#666" }}>
            <span>
               {Localization.getText("Farmland")}: {formatPercentage(analysisResult.farmlandOverlapPercentage)}
            </span>
            <span>
               {Localization.getText("Non-Farmland")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
            </span>
         </div>
      </div>
   );
}

export function JPBPolygonAnalyzer(props: JPBPolygonAnalyzerProps) {
   const { state: mapitState } = useContext(MapitStateContext);
   const [analyzerState, setAnalyzerState] = useJPBSessionSlice("analyzer");
   const assumptions = useMemo(
      () => getResolvedJPBAssumptions(props.customAssumptions),
      [props.customAssumptions, props.settingsRefreshToken]
   );
   const soilFactorsByGroup = useMemo(() => getSoilQualityFactorsByGroup(assumptions), [assumptions]);
   const nonAgriToAgriRatio = useMemo(() => getNonAgricultureToAgricultureRatio(assumptions), [assumptions]);
   const selectedLayerId = analyzerState.selectedLayerId;
   const analysisResult = analyzerState.analysisResult;
   const isCalculating = analyzerState.isCalculating;
   const isFetchingGeometry = analyzerState.isFetchingGeometry;
   const geometryData = analyzerState.geometryData;
   const farmlandIntersectionGeometry = analyzerState.farmlandIntersectionGeometry;
   const jordbundskortIntersectionGeometry = analyzerState.jordbundskortIntersectionGeometry;
   const analysisPropertyOutlineGeometry = analyzerState.analysisPropertyOutlineGeometry;
   const hoveredSoilType = analyzerState.hoveredSoilType;
   const hoveredSoilPercentage = analyzerState.hoveredSoilPercentage;
   const hoveredNonFarmland = analyzerState.hoveredNonFarmland;
   const setSelectedLayerId = useCallback((value: number | undefined) => {
      setAnalyzerState((prev) => ({ ...prev, selectedLayerId: value }));
   }, [setAnalyzerState]);
   const setAnalysisResult = useCallback((value: JPBAnalyzerResult | null) => {
      setAnalyzerState((prev) => ({ ...prev, analysisResult: value }));
   }, [setAnalyzerState]);
   const setIsCalculating = useCallback((value: boolean) => {
      setAnalyzerState((prev) => ({ ...prev, isCalculating: value }));
   }, [setAnalyzerState]);
   const setIsFetchingGeometry = useCallback((value: boolean) => {
      setAnalyzerState((prev) => ({ ...prev, isFetchingGeometry: value }));
   }, [setAnalyzerState]);
   const setGeometryData = useCallback((value: JPBAnalyzerGeometryData | null) => {
      setAnalyzerState((prev) => ({ ...prev, geometryData: value }));
   }, [setAnalyzerState]);
   const setFarmlandIntersectionGeometry = useCallback((value: any | null) => {
      setAnalyzerState((prev) => ({ ...prev, farmlandIntersectionGeometry: value }));
   }, [setAnalyzerState]);
   const setJordbundskortIntersectionGeometry = useCallback((value: any | null) => {
      setAnalyzerState((prev) => ({ ...prev, jordbundskortIntersectionGeometry: value }));
   }, [setAnalyzerState]);
   const setAnalysisPropertyOutlineGeometry = useCallback((value: any | null) => {
      setAnalyzerState((prev) => ({ ...prev, analysisPropertyOutlineGeometry: value }));
   }, [setAnalyzerState]);
   const setHoveredSoilType = useCallback((value: string | null) => {
      setAnalyzerState((prev) => ({ ...prev, hoveredSoilType: value }));
   }, [setAnalyzerState]);
   const setHoveredSoilPercentage = useCallback((value: number | null) => {
      setAnalyzerState((prev) => ({ ...prev, hoveredSoilPercentage: value }));
   }, [setAnalyzerState]);
   const setHoveredNonFarmland = useCallback((value: boolean) => {
      setAnalyzerState((prev) => ({ ...prev, hoveredNonFarmland: value }));
   }, [setAnalyzerState]);
   const calculationTimeoutMs = useMemo(() => {
      const configured = Number(SettingsManager.getSystemSetting("jpb.polygonAnalyzerTimeoutMs", 90000));
      if (!Number.isFinite(configured)) return 90000;
      return Math.max(5000, Math.floor(configured));
   }, [props.settingsRefreshToken]);

   // Cache for layers that failed due to timeout - prevents retrying
   const failedLayersRef = useRef<Set<number>>(new Set());
   const timeoutRef = useRef<NodeJS.Timeout | null>(null);
   const trackedPolygonSignatureRef = useRef<string | null>(null);

   const analyzerLayers = useMemo(() => {
      return Object.values(mapitState.layers)
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .filter(layer => {
            if (MapitUtils.isPointLayer(layer.type)) {
               return !!(layer.geoJson?.features?.length);
            }
            return MapitUtils.isAreaLayer(layer.type) ||
               layer.type === "GeoJSON_Polygon" ||
               (layer.geoJson && layer.geoJson.features && layer.geoJson.features.length > 0 &&
                  layer.geoJson.features[0]?.geometry?.type === "Polygon");
         });
   }, [mapitState.layers]);

   const selectedLayer = useMemo(() => {
      if (selectedLayerId === undefined) return null;
      return mapitState.layers[selectedLayerId] || null;
   }, [selectedLayerId, mapitState.layers]);

   const pointLayerGeometryKey = useMemo(() => {
      if (!selectedLayer || !MapitUtils.isPointLayer(selectedLayer.type)) {
         return "";
      }
      try {
         return JSON.stringify(selectedLayer.geoJson);
      } catch {
         return String(selectedLayer.layerId);
      }
   }, [selectedLayer]);

   const selectedPolygon = useMemo(() => {
      if (!selectedLayer || !selectedLayer.geoJson || !selectedLayer.geoJson.features || selectedLayer.geoJson.features.length === 0) {
         return null;
      }

      const polygonFeatures = selectedLayer.geoJson.features.filter((f: any) =>
         f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon")
      );

      if (polygonFeatures.length === 0) {
         return null;
      }

      if (polygonFeatures.length === 1) {
         return polygonFeatures[0];
      }

      try {
         const featureCollectionToCombine = featureCollection(polygonFeatures);
         const combined = combine(featureCollectionToCombine as any);

         if (combined && combined.features && combined.features.length > 0) {
            const combinedGeometry = combined.features[0]?.geometry;
            if (combinedGeometry) {
               return {
                  type: "Feature" as const,
                  geometry: combinedGeometry,
                  properties: {}
               };
            }
         }
      } catch (error) {
         console.error("Error combining polygon features:", error);
         return polygonFeatures[0];
      }

      return polygonFeatures[0];
   }, [selectedLayer]);

   const checkedProperties = useMemo(
      () => props.referenceProperties.filter((p) => props.checkedItems.has(p.documentID)),
      [props.referenceProperties, props.checkedItems]
   );

   const avgEffectiveMarkPrice = useMemo(
      () =>
         calculateWeightedMeanForSeries(checkedProperties, "effectiveMark", {
            delta: nonAgriToAgriRatio,
            averageCalculationMethod: props.averageCalculationMethod
         }) ?? 0,
      [checkedProperties, nonAgriToAgriRatio, props.averageCalculationMethod]
   );

   const soilPricesForDisplay = useMemo(
      () =>
         calculateSoilSpecificAgriculturalPrices(
            checkedProperties,
            nonAgriToAgriRatio,
            soilFactorsByGroup,
            props.averageCalculationMethod
         ),
      [checkedProperties, nonAgriToAgriRatio, soilFactorsByGroup, props.averageCalculationMethod]
   );

   const pedagogicalBreakdown = useMemo(() => {
      if (!analysisResult || analysisResult.estimatedPricePerSquareMeter === null || !soilPricesForDisplay) {
         return null;
      }

      const alpha = analysisResult.farmlandOverlapPercentage / 100;
      const delta = nonAgriToAgriRatio;
      const soilFractionsByGroup: { [soilGroup: string]: number } = {};
      for (const [soilType, percentage] of Object.entries(analysisResult.soilComposition)) {
         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) continue;
         soilFractionsByGroup[soilGroup] = (soilFractionsByGroup[soilGroup] ?? 0) + (percentage / 100);
      }

      const totalFraction = Object.values(soilFractionsByGroup).reduce((sum, v) => sum + v, 0);
      if (totalFraction <= 0) return null;

      const normalizationFactor = Math.abs(totalFraction - 1) > 0.01 ? (1 / totalFraction) : 1;
      const rows = Object.entries(soilFractionsByGroup)
         .map(([group, fraction]) => {
            const normalizedFraction = fraction * normalizationFactor;
            const price = soilPricesForDisplay[group as SoilQualityGroup];
            if (price === undefined) return null;
            return {
               group,
               fraction: normalizedFraction,
               price,
               contribution: normalizedFraction * price
            };
         })
         .filter((row): row is { group: string; fraction: number; price: number; contribution: number } => row !== null)
         .sort((a, b) => b.fraction - a.fraction);

      if (rows.length === 0) return null;

      const pAgri = rows.reduce((sum, row) => sum + row.contribution, 0);
      const otherPrice = pAgri * delta;
      const mixFactor = alpha + (1 - alpha) * delta;
      const pTotal = pAgri * mixFactor;

      return {
         alpha,
         delta,
         pAgri,
         otherPrice,
         mixFactor,
         pTotal,
         rows,
         checkedTransactions: checkedProperties.length,
         usedNormalization: Math.abs(totalFraction - 1) > 0.01,
         rawFractionSum: totalFraction
      };
   }, [analysisResult, soilPricesForDisplay, nonAgriToAgriRatio, checkedProperties.length]);

   const displayedMarkPrice = useMemo(() => {
      if (pedagogicalBreakdown?.pAgri && pedagogicalBreakdown.pAgri > 0) {
         return pedagogicalBreakdown.pAgri;
      }
      return avgEffectiveMarkPrice > 0 ? avgEffectiveMarkPrice : null;
   }, [pedagogicalBreakdown, avgEffectiveMarkPrice]);

   const displayedOtherPrice = displayedMarkPrice !== null ? displayedMarkPrice * nonAgriToAgriRatio : null;

   const onAnalysisChange = props.onAnalysisChange;
   const subjectAddress = props.subjectAddress;
   useEffect(() => {
      if (!onAnalysisChange) return;
      if (
         !analysisResult ||
         analysisResult.error ||
         analysisResult.estimatedPricePerSquareMeter === null ||
         displayedMarkPrice === null ||
         !pedagogicalBreakdown
      ) {
         onAnalysisChange(null);
         return;
      }
      const reportAnalysis: JPBReportPolygonAnalysis = {
         subjectAddress,
         polygonGeoJson: analysisPropertyOutlineGeometry ?? undefined,
         polygonAreaM2: analysisResult.polygonArea,
         estimatedPricePerSquareMeter: analysisResult.estimatedPricePerSquareMeter
      };
      onAnalysisChange(reportAnalysis);
   }, [onAnalysisChange, analysisResult, pedagogicalBreakdown, displayedMarkPrice, subjectAddress, analysisPropertyOutlineGeometry]);

   const formatExactNumber = (value: number, maxDigits: number = 6): string => {
      return value.toLocaleString(Localization.getLocale(), {
         minimumFractionDigits: 0,
         maximumFractionDigits: maxDigits
      });
   };
   const formatCompactNumber = (value: number): string => formatExactNumber(value, 3);
   const formatCompactPercent = (value: number): string => `${formatExactNumber(value, 2)}%`;
   const renderAlignedFormulaBlock = (rows: Array<{ left: string; right?: string }>) => (
      <div
         style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "12.5px",
            color: "#2f6e68",
            whiteSpace: "pre-wrap",
            paddingLeft: "2px",
            marginTop: "4px",
            marginBottom: "2px",
            display: "grid",
            gridTemplateColumns: "max-content 16px 1fr",
            columnGap: "4px",
            alignItems: "baseline",
            lineHeight: 1.8
         }}
      >
         {rows.map((row, index) => {
            const isResult = index === rows.length - 1 && row.right !== undefined;
            const resultStyle: React.CSSProperties = isResult ? { color: "#2f6e68", fontWeight: 600 } : {};
            return (
               <React.Fragment key={`${row.left}-${index}`}>
                  <span style={resultStyle}>{row.left}</span>
                  <span style={{ textAlign: "center", color: "#2f6e68", fontWeight: 600 }}>
                     {row.right !== undefined ? "=" : ""}
                  </span>
                  <span style={resultStyle}>{row.right ?? ""}</span>
               </React.Fragment>
            );
         })}
      </div>
   );

   useEffect(() => {
      let effectActive = true;

      const clearGeometry = () => {
         setGeometryData(null);
         setFarmlandIntersectionGeometry(null);
         setJordbundskortIntersectionGeometry(null);
         setAnalysisPropertyOutlineGeometry(null);
         setAnalysisResult(null);
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
      };

      if (props.referenceProperties.length === 0 || props.checkedItems.size === 0) {
         clearGeometry();
         return;
      }

      if (selectedLayerId === undefined || !selectedLayer) {
         clearGeometry();
         return;
      }

      const isPointLayer = MapitUtils.isPointLayer(selectedLayer.type);

      if (!isPointLayer && !selectedPolygon) {
         clearGeometry();
         return;
      }

      if (isPointLayer && !pointLayerGeometryKey) {
         clearGeometry();
         return;
      }

      if (failedLayersRef.current.has(selectedLayerId)) {
         setAnalysisPropertyOutlineGeometry(null);
         setGeometryData({
            polygonArea: 0,
            farmlandOverlapArea: 0,
            farmlandOverlapPercentage: 0,
            soilComposition: {},
            error: "Cannot calculate - calculation timeout"
         });
         setIsFetchingGeometry(false);
         return;
      }

      setIsFetchingGeometry(true);

      (async () => {
         try {
            let polygonFeatureForAnalysis: any = selectedPolygon;

            if (isPointLayer) {
               const { feature, error } = await fetchCombinedSfePolygonFromPointLayer(selectedLayer);
               if (!effectActive) {
                  return;
               }
               if (!feature) {
                  setFarmlandIntersectionGeometry(null);
                  setJordbundskortIntersectionGeometry(null);
                  setAnalysisPropertyOutlineGeometry(null);
                  setGeometryData({
                     polygonArea: 0,
                     farmlandOverlapArea: 0,
                     farmlandOverlapPercentage: 0,
                     soilComposition: {},
                     error: error || Localization.getText("Could not resolve SFE geometry from pins")
                  });
                  return;
               }
               polygonFeatureForAnalysis = feature;
            }

            if (!polygonFeatureForAnalysis?.geometry) {
               if (effectActive) {
                  setAnalysisPropertyOutlineGeometry(null);
               }
               return;
            }

            const polygonGeometry = polygonFeatureForAnalysis.geometry;
            const polygonAreaM2 = area({
               type: "Feature" as const,
               geometry: polygonGeometry,
               properties: {}
            });

            const MAX_AREA_M2 = 10_000_000;
            if (polygonAreaM2 > MAX_AREA_M2) {
               const areaHa = polygonAreaM2 / 10_000;
               if (effectActive) {
                  setAnalysisPropertyOutlineGeometry(null);
                  setGeometryData({
                     polygonArea: polygonAreaM2,
                     farmlandOverlapArea: 0,
                     farmlandOverlapPercentage: 0,
                     soilComposition: {},
                     error: Localization.getFormattedText("Polygon area too large ({area} ha). Maximum allowed: 1000 ha. Please select a smaller polygon.", { area: areaHa.toFixed(1) })
                  });
               }
               return;
            }

            if (effectActive) {
               setAnalysisPropertyOutlineGeometry(polygonGeometry);
            }

            const checkedPropertiesForGeometry = props.referenceProperties.filter((p) => props.checkedItems.has(p.documentID));
            const averageDate = checkedPropertiesForGeometry.length > 0
               ? new Date(checkedPropertiesForGeometry.reduce((sum, p) => sum + p.transactionDate.getTime(), 0) / checkedPropertiesForGeometry.length)
               : new Date();

            const timeoutPromise = new Promise<never>((_, reject) => {
               timeoutRef.current = setTimeout(() => {
                  reject(new Error("Calculation timeout - area too large to calculate"));
               }, calculationTimeoutMs);
            });

            const calculationPromise = (async () => {
               let farmlandGeometry: any | null = null;
               let farmlandIntersectionResult: { intersectionGeometry: any; overlapPercentage: number } | null = null;

               try {
                  farmlandGeometry = await fetchFarmlandOverlap(
                     polygonGeometry,
                     averageDate
                  );

                  if (farmlandGeometry) {
                     farmlandIntersectionResult = await runWithYield(() => calculateFarmlandIntersection(
                        polygonGeometry as any,
                        farmlandGeometry as any
                     ));
                  }
               } catch (error) {
                  console.error("Error fetching farmland overlap:", error);
               }

               await yieldToMainThread();

               const farmlandOverlapAreaM2 = farmlandIntersectionResult?.intersectionGeometry
                  ? area({
                     type: "Feature" as const,
                     geometry: farmlandIntersectionResult.intersectionGeometry,
                     properties: {}
                  })
                  : 0;

               const farmlandOverlapPercentage = farmlandIntersectionResult?.overlapPercentage ?? 0;

               let soilComposition: { [soilType: string]: number } = {};
               let jordbundskortIntersection: any | null = null;

               try {
                  const jordbundskortGeometry = await fetchJordbundskortOverlap(
                     polygonGeometry
                  );

                  const farmlandIntersectionGeom = farmlandIntersectionResult?.intersectionGeometry;
                  if (farmlandIntersectionGeom && jordbundskortGeometry) {
                     const intersectionResult = await calculateJordbundskortFarmlandIntersection(
                        farmlandIntersectionGeom as any,
                        jordbundskortGeometry as any
                     );

                     if (intersectionResult) {
                        soilComposition = intersectionResult.overlapPercentages;
                        jordbundskortIntersection = intersectionResult.intersectionGeometry;
                     }
                  }
               } catch (error) {
                  console.error("Error fetching jordbundskort overlap:", error);
               }

               await yieldToMainThread();

               return {
                  farmlandIntersectionGeometry: farmlandIntersectionResult?.intersectionGeometry || null,
                  jordbundskortIntersection: jordbundskortIntersection || null,
                  geometryData: {
                     polygonArea: polygonAreaM2,
                     farmlandOverlapArea: farmlandOverlapAreaM2,
                     farmlandOverlapPercentage: farmlandOverlapPercentage,
                     soilComposition: soilComposition
                  }
               };
            })();

            const result = await Promise.race([calculationPromise, timeoutPromise]);

            if (!effectActive) {
               return;
            }

            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            setFarmlandIntersectionGeometry(result.farmlandIntersectionGeometry);
            setJordbundskortIntersectionGeometry(result.jordbundskortIntersection);
            setGeometryData(result.geometryData);
         } catch (error: any) {
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            if (effectActive) {
               setAnalysisPropertyOutlineGeometry(null);
            }

            if (error.message && error.message.includes("timeout")) {
               if (selectedLayerId !== undefined) {
                  failedLayersRef.current.add(selectedLayerId);
               }
               console.warn("Calculation timeout for layer:", selectedLayerId);
               if (effectActive) {
                  setGeometryData({
                     polygonArea: 0,
                     farmlandOverlapArea: 0,
                     farmlandOverlapPercentage: 0,
                     soilComposition: {},
                     error: "Cannot calculate - calculation timeout"
                  });
               }
            } else {
               console.error("Error fetching geometry data:", error);
               if (effectActive) {
                  setGeometryData({
                     polygonArea: 0,
                     farmlandOverlapArea: 0,
                     farmlandOverlapPercentage: 0,
                     soilComposition: {},
                     error: error.message || "Unknown error"
                  });
               }
            }
         } finally {
            if (effectActive) {
               setIsFetchingGeometry(false);
            }
         }
      })();

      return () => {
         effectActive = false;
         setAnalysisPropertyOutlineGeometry(null);
         setIsFetchingGeometry(false);
      };
   }, [
      selectedPolygon,
      selectedLayerId,
      selectedLayer,
      pointLayerGeometryKey,
      calculationTimeoutMs,
      props.referenceProperties,
      props.checkedItems
   ]);

   useEffect(() => {
      if (selectedLayerId === undefined) return
      ActionTracker.track("JPBModule", "Action", "SelectLayerForSimulation");
   }, [selectedLayerId]);

   useEffect(() => {
      if (selectedLayerId === undefined || !selectedLayer) {
         return;
      }
      if (MapitUtils.isPointLayer(selectedLayer.type)) {
         const pinCount = collectLngLatsFromPointLayer(selectedLayer).length;
         const pinSignature = `pin|${selectedLayerId}|${pointLayerGeometryKey}`;
         if (trackedPolygonSignatureRef.current === pinSignature) {
            return;
         }
         trackedPolygonSignatureRef.current = pinSignature;
         ActionTracker.track("JPBModule", "Action", "UsePolygonForSimulation", {
            layerID: String(selectedLayerId),
            layerType: String(selectedLayer.type ?? ""),
            mode: `source=sfeFromPins;pinCount=${pinCount}`
         });
         return;
      }
      if (!selectedPolygon) {
         return;
      }
      const polygonAreaM2 = area({
         type: "Feature" as const,
         geometry: selectedPolygon.geometry,
         properties: {}
      });
      const geometry = selectedPolygon.geometry as any;
      const vertexCount = geometry?.type === "Polygon"
         ? (geometry.coordinates?.[0]?.length ?? 0)
         : geometry?.type === "MultiPolygon"
            ? (geometry.coordinates || []).reduce((sum: number, polygon: any) => sum + (polygon?.[0]?.length ?? 0), 0)
            : 0;
      const polygonSignature = `${selectedLayerId}|${selectedPolygon.geometry?.type ?? "unknown"}|${Math.round(polygonAreaM2)}|${vertexCount}`;
      if (trackedPolygonSignatureRef.current === polygonSignature) {
         return;
      }
      trackedPolygonSignatureRef.current = polygonSignature;
      ActionTracker.track("JPBModule", "Action", "UsePolygonForSimulation", {
         layerID: String(selectedLayerId),
         layerType: String(selectedLayer?.type ?? ""),
         mode: `polygonType=${selectedPolygon.geometry?.type ?? "unknown"};areaM2=${Math.round(polygonAreaM2)};vertices=${vertexCount}`
      });
   }, [selectedPolygon, selectedLayerId, selectedLayer, pointLayerGeometryKey]);

   // Cleanup timeout and reset failed layers cache when layer changes
   useEffect(() => {
      // Clear failed layers cache when switching layers - allows retry with different polygon
      failedLayersRef.current.clear();

      return () => {
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
      };
   }, [selectedLayerId]);

   useEffect(() => {
      if (props.lockedAnalyzerLayerId !== undefined) {
         setSelectedLayerId(props.lockedAnalyzerLayerId);
      }
   }, [props.lockedAnalyzerLayerId]);

   useEffect(() => {
      if (!geometryData || checkedProperties.length === 0) {
         setAnalysisResult(null);
         return;
      }

      setIsCalculating(true);
      let cancelled = false;

      (async () => {
         await yieldToMainThread();

         try {
            const soilPrices = calculateSoilSpecificAgriculturalPrices(
               checkedProperties,
               nonAgriToAgriRatio,
               soilFactorsByGroup,
               props.averageCalculationMethod
            );
            await yieldToMainThread();

            let estimatedPrice: number | null = null;
            if (soilPrices && geometryData.farmlandOverlapPercentage > 0 && Object.keys(geometryData.soilComposition).length > 0) {
               try {
                  const agriFraction = geometryData.farmlandOverlapPercentage / 100;
                  const soilCompositionByGroup: { [soilGroup: string]: number } = {};

                  for (const [soilType, percentage] of Object.entries(geometryData.soilComposition)) {
                     const soilGroup = mapSoilTypeToGroup(soilType);
                     if (soilGroup && SOIL_QUALITY_GROUPS.includes(soilGroup)) {
                        const fraction = percentage / 100;
                        if (soilCompositionByGroup[soilGroup]) {
                           soilCompositionByGroup[soilGroup] += fraction;
                        } else {
                           soilCompositionByGroup[soilGroup] = fraction;
                        }
                     }
                  }

                  const totalFraction = Object.values(soilCompositionByGroup).reduce((a, b) => a + b, 0);
                  if (totalFraction > 0 && Math.abs(totalFraction - 1) > 0.01) {
                     const normalizationFactor = 1 / totalFraction;
                     for (const group in soilCompositionByGroup) {
                        soilCompositionByGroup[group] *= normalizationFactor;
                     }
                  }

                  await yieldToMainThread();

                  const hasAllPrices = Object.keys(soilCompositionByGroup).every(
                     group => soilPrices[group as SoilQualityGroup] !== undefined
                  );

                  if (hasAllPrices && Object.keys(soilCompositionByGroup).length > 0) {
                     estimatedPrice = calculateAreaPrice(
                        agriFraction,
                        soilCompositionByGroup,
                        soilPrices as { [soilGroup: string]: number },
                        nonAgriToAgriRatio
                     );
                  }
               } catch (e) {
                  console.warn("Failed to calculate estimated price:", e);
               }
            }

            if (cancelled) return;
            setAnalysisResult({
               polygonArea: geometryData.polygonArea,
               farmlandOverlapArea: geometryData.farmlandOverlapArea,
               farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
               soilComposition: geometryData.soilComposition,
               estimatedPricePerSquareMeter: estimatedPrice,
               error: geometryData.error
            });
         } catch (error: any) {
            if (cancelled) return;
            console.error("Error calculating price:", error);
            setAnalysisResult({
               polygonArea: geometryData.polygonArea,
               farmlandOverlapArea: geometryData.farmlandOverlapArea,
               farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
               soilComposition: geometryData.soilComposition,
               estimatedPricePerSquareMeter: null,
               error: error.message || "Unknown error"
            });
         } finally {
            if (!cancelled) {
               setIsCalculating(false);
            }
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [
      geometryData,
      checkedProperties,
      nonAgriToAgriRatio,
      soilFactorsByGroup,
      props.averageCalculationMethod,
      props.customAssumptions,
      props.settingsRefreshToken
   ]);

   useEffect(() => {
      if (!mapitState.map) return;
      const map = mapitState.map.getMapPop();
      if (!map) return;


      const farmlandGeoJson = farmlandIntersectionGeometry
         ? {
            type: "FeatureCollection" as const,
            features: [{
               type: "Feature" as const,
               geometry: farmlandIntersectionGeometry,
               properties: {}
            }]
         }
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND) as GeoJSONSource;
         if (source) source.setData(farmlandGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND, {
            type: 'geojson',
            data: farmlandGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
         const farmlandLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'fill-pattern': "mit-patt-char-🚜-1",
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
            }
         };
         map.addLayer(farmlandLayerSpec);
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
         const farmlandOutlineLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline",
            type: 'line',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'line-color': '#ff6b00',
               'line-opacity': ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1],
               'line-width': 2
            }
         };
         map.addLayer(farmlandOutlineLayerSpec);
      }

      const jordbundskortGeoJson = jordbundskortIntersectionGeometry
         ? (jordbundskortIntersectionGeometry.type === "FeatureCollection"
            ? jordbundskortIntersectionGeometry
            : {
               type: "FeatureCollection" as const,
               features: [{
                  type: "Feature" as const,
                  geometry: jordbundskortIntersectionGeometry,
                  properties: {}
               }]
            })
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (jordbundskortGeoJson.type === "FeatureCollection" && jordbundskortGeoJson.features) {
         jordbundskortGeoJson.features = jordbundskortGeoJson.features.map((feature: any) => ({
            ...feature,
            properties: {
               ...feature.properties,
               color: getSoilTypeColor(feature.properties?.Jordtype || feature.properties?.jordtype)
            }
         }));
      }

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT) as GeoJSONSource;
         if (source) source.setData(jordbundskortGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT, {
            type: 'geojson',
            data: jordbundskortGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const jordbundskortLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            paint: {
               'fill-color': ['get', 'color'],
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.7],
               'fill-antialias': false,
               'fill-outline-color': '#00000000'
            }
         };
         map.addLayer(jordbundskortLayerSpec);
      }

      const propertyOutlineGeoJson = analysisPropertyOutlineGeometry
         ? {
            type: "FeatureCollection" as const,
            features: [{
               type: "Feature" as const,
               geometry: analysisPropertyOutlineGeometry,
               properties: {}
            }]
         }
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_PROPERTY)) {
         const propertySource = map.getSource(SOURCE_ID_POLYGON_ANALYZER_PROPERTY) as GeoJSONSource;
         if (propertySource) {
            propertySource.setData(propertyOutlineGeoJson as any);
         }
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_PROPERTY, {
            type: 'geojson',
            data: propertyOutlineGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_PROPERTY_OUTLINE)) {
         const propertyOutlineSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_PROPERTY_OUTLINE,
            type: 'line',
            source: SOURCE_ID_POLYGON_ANALYZER_PROPERTY,
            paint: {
               'line-color': JPB_ANALYZER_PROPERTY_OUTLINE_COLOR,
               'line-width': JPB_ANALYZER_PROPERTY_OUTLINE_WIDTH_PX,
               'line-opacity': 1
            }
         };
         map.addLayer(propertyOutlineSpec);
      }

      return () => {
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_PROPERTY_OUTLINE)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_PROPERTY_OUTLINE);
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline");
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_PROPERTY)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_PROPERTY);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND);
         }
      };
   }, [mapitState.map, farmlandIntersectionGeometry, jordbundskortIntersectionGeometry, analysisPropertyOutlineGeometry]);

   return (
      <div className="jpb-polygon-analyzer-root">
         <div className="jpb-card jpb-card--padded" style={{ overflow: "hidden" }}>
            <h3 style={{ marginTop: 0, marginBottom: "15px", fontSize: "18px", fontWeight: "600" }}>
               {props.lockedAnalyzerLayerId !== undefined
                  ? Localization.getText("Prisanalyse")
                  : Localization.getText("Polygon Price Analyzer")}
            </h3>

            {props.lockedAnalyzerLayerId === undefined && (
               analyzerLayers.length > 0 ? (
                  <GlassInputGroup>
                     <div className="GlassInputLabel" style={{ marginBottom: "10px" }}>
                        {Localization.getText("Select polygon or pin layer")}
                     </div>
                     <div style={{ marginBottom: "15px" }}>
                        <SelectLayer
                           selectedLayerId={selectedLayerId || ""}
                           onSelect={(layerId) => {
                              setSelectedLayerId(layerId);
                           }}
                           availableLayers={analyzerLayers}
                        />
                     </div>
                  </GlassInputGroup>
               ) : (
                  <div style={{ marginTop: "5px", color: "#666", fontStyle: "italic" }}>
                     {Localization.getText("Create or define a polygon or pin layer to use the analyzer")}
                  </div>
               )
            )}

            {(isFetchingGeometry || (isCalculating && !analysisResult)) && (
               <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                  <Spinner size="sm" />
                  <span>
                     {isFetchingGeometry
                        ? Localization.getText("Fetching geometry data...")
                        : Localization.getText("Calculating price...")}
                  </span>
               </div>
            )}

            {analysisResult && !isFetchingGeometry && (
               <div style={{ marginTop: "20px" }}>
                  {analysisResult.error ? (
                     <div style={{ color: "#d32f2f", marginBottom: "10px", padding: "10px", backgroundColor: "#ffebee", borderRadius: "4px", border: "1px solid #ffcdd2" }}>
                        <strong>{Localization.getText("Error")}:</strong> {analysisResult.error}
                     </div>
                  ) : (
                     <>
                        {analysisResult.farmlandOverlapPercentage > 0 && (
                           <SoilDistributionBar
                              analysisResult={analysisResult}
                              hoveredSoilType={hoveredSoilType}
                              hoveredSoilPercentage={hoveredSoilPercentage}
                              hoveredNonFarmland={hoveredNonFarmland}
                              setHoveredSoilType={setHoveredSoilType}
                              setHoveredSoilPercentage={setHoveredSoilPercentage}
                              setHoveredNonFarmland={setHoveredNonFarmland}
                           />
                        )}
                        <div style={{
                           marginTop: "20px",
                           border: "1px solid #ddd",
                           borderRadius: "4px",
                           overflow: "hidden"
                        }}>
                           <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                              <thead>
                                 <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
                                    <th style={{ padding: "10px", textAlign: "left", fontWeight: "600" }}>
                                       {Localization.getText("Category")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("M_Area")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Percentage")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600" }}>
                                       {Localization.getText("Total Polygon Area")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       -
                                    </td>
                                 </tr>

                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                       {Localization.getText("Farmland (Mark)")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.farmlandOverlapArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(analysisResult.farmlandOverlapPercentage)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {displayedMarkPrice !== null
                                          ? formatPricePerSquareMeter(displayedMarkPrice, props.areaUnit)
                                          : "-"}
                                    </td>
                                 </tr>

                                 {Object.entries(analysisResult.soilComposition)
                                    .sort(([a], [b]) => {
                                       const groupA = mapSoilTypeToGroup(a);
                                       const groupB = mapSoilTypeToGroup(b);
                                       if (!groupA || !groupB) return 0;
                                       const indexA = soilFactorsByGroup[groupA] || 0;
                                       const indexB = soilFactorsByGroup[groupB] || 0;
                                       return indexB - indexA;
                                    })
                                    .map(([soilType, percentage]) => {
                                       const soilGroup = mapSoilTypeToGroup(soilType);
                                       const soilAreaM2 = (analysisResult.farmlandOverlapArea * percentage) / 100;
                                       const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                                       const soilPrice = soilGroup && soilPricesForDisplay ? soilPricesForDisplay[soilGroup] : undefined;

                                       return (
                                          <tr key={soilType} style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                             <td style={{ padding: "10px", paddingLeft: "40px", fontStyle: "italic" }}>
                                                {soilType} {soilGroup && `(${soilGroup})`}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {formatArea(soilAreaM2, props.areaUnit)}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {formatPercentage(percentageOfTotal)}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {soilPrice !== undefined
                                                   ? formatPricePerSquareMeter(soilPrice, props.areaUnit)
                                                   : "-"}
                                             </td>
                                          </tr>
                                       );
                                    })}

                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                       {Localization.getText("Non-Farmland (Non-Mark)")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea - analysisResult.farmlandOverlapArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {displayedOtherPrice !== null
                                          ? formatPricePerSquareMeter(displayedOtherPrice, props.areaUnit)
                                          : "-"}
                                    </td>
                                 </tr>

                                 {analysisResult.estimatedPricePerSquareMeter !== null && (
                                    <tr style={{ borderTop: "2px solid #3a8e87", backgroundColor: "#fff" }}>
                                       <td style={{ padding: "15px", fontWeight: "600", fontSize: "16px" }}>
                                          {Localization.getText("Estimated Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                       </td>
                                       <td colSpan={2} style={{ padding: "15px" }}></td>
                                       <td style={{ padding: "15px", textAlign: "right", fontSize: "16px", fontWeight: "600", color: "#3a8e87" }}>
                                          {formatPricePerSquareMeter(analysisResult.estimatedPricePerSquareMeter, props.areaUnit)}
                                       </td>
                                    </tr>
                                 )}
                              </tbody>
                           </table>
                        </div>

                        {pedagogicalBreakdown && (
                           <details style={{ marginTop: "14px", border: "1px solid #d8e8e6", borderRadius: "6px", background: "#f8fcfb" }}>
                              <summary style={{ cursor: "pointer", padding: "10px 12px", fontWeight: 600, color: "#2f6e68" }}>
                                 {Localization.getText("How this estimate is calculated")}
                              </summary>
                              <div style={{ padding: "4px 14px 14px 14px", fontSize: "13px", lineHeight: 1.55 }}>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("1) JB prices (where they come from)")}
                                    </div>
                                    <span style={{ color: "#111" }}>
                                       {Localization.getText("Computed from checked reference transactions using the soil model and current assumptions.")}{" "}
                                       ({pedagogicalBreakdown.checkedTransactions} {Localization.getText("transactions")}).
                                    </span>
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("2) Polygon farmland soil mix")}
                                    </div>
                                    <span style={{ color: "#111" }}>
                                       {pedagogicalBreakdown.rows.map((r) => `${r.group}: ${formatCompactPercent(r.fraction * 100)}`).join(" · ")}
                                       {pedagogicalBreakdown.usedNormalization && (
                                          <span> ({Localization.getText("normalized from")} {formatCompactPercent(pedagogicalBreakdown.rawFractionSum * 100)})</span>
                                       )}
                                    </span>
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("3) Farmland base price (P_agri)")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Average soil-group price for farmland in this polygon.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Farmland price"),
                                          right: Localization.getText("average soil-group price")
                                       },
                                       {
                                          left: "",
                                          right: `Σ(${Localization.getText("Jordgruppe andel")} · ${Localization.getText("soil-group price")})`
                                       },
                                       {
                                          left: "",
                                          right: pedagogicalBreakdown.rows.map((r) => `${formatExactNumber(r.fraction, 4)} · ${formatExactNumber(r.price, 3)}`).join(" + ")
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.pAgri, props.areaUnit)})`
                                       }
                                    ])}
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("4) Non-farmland deduced from ratio")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Non-farmland price is derived from delta.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Non-farmland price"),
                                          right: `${Localization.getText("Farmland price")} · delta`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} · ${formatExactNumber(pedagogicalBreakdown.delta, 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.otherPrice, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.otherPrice, props.areaUnit)})`
                                       }
                                    ])}
                                 </div>
                                 <div>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("5) Final estimate")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Final estimate combines farmland share and non-farmland ratio.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Total estimated price"),
                                          right: `${Localization.getText("Price on farmland")} · ${Localization.getText("Mark andel")} + ${Localization.getText("Price on non-farmland")} · ${Localization.getText("Ikke-mark andel")}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} · ${formatExactNumber(pedagogicalBreakdown.alpha, 3)} + ${formatExactNumber(pedagogicalBreakdown.otherPrice, 3)} · ${formatExactNumber(1 - pedagogicalBreakdown.alpha, 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri * pedagogicalBreakdown.alpha, 3)} + ${formatExactNumber(pedagogicalBreakdown.otherPrice * (1 - pedagogicalBreakdown.alpha), 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pTotal, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.pTotal, props.areaUnit)})`
                                       }
                                    ])}
                                    <div style={{
                                       marginTop: "10px",
                                       color: "#111",
                                       fontSize: "12px"
                                    }}>
                                       <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>
                                          {Localization.getText("Andel af mark")} = {formatCompactNumber(pedagogicalBreakdown.alpha)}
                                       </span>
                                       {" · "}
                                       <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>
                                          {Localization.getText("Ikke-mark-forhold")} = {formatCompactNumber(pedagogicalBreakdown.delta)}
                                       </span>
                                    </div>
                                 </div>
                              </div>
                           </details>
                        )}

                        {analysisResult.estimatedPricePerSquareMeter === null && (
                           <div style={{ marginTop: "20px", color: "#666", fontStyle: "italic" }}>
                              {Localization.getText("Unable to calculate estimated price. Ensure the polygon overlaps with farmland and soil data.")}
                           </div>
                        )}
                     </>
                  )}
               </div>
            )}

            {!selectedLayerId && props.lockedAnalyzerLayerId === undefined && analyzerLayers.length > 0 && !isFetchingGeometry && !isCalculating && (
               <div style={{ marginTop: "15px", color: "#666", fontStyle: "italic" }}>
                  {Localization.getText("Select a polygon layer to analyze")}
               </div>
            )}
         </div>
      </div>
   );
}